/**
 * Signed-URL helpers for private Supabase Storage buckets.
 *
 * Background: the app historically read files via PUBLIC storage URLs
 * (`.../object/public/<bucket>/<path>` or `getPublicUrl()`). Those 400 once a
 * bucket is made private. These helpers convert a path (or an already-built
 * public URL) into a short-lived SIGNED URL, which works on private buckets.
 *
 * This mirrors the re-signing logic AudioPlayer.js already uses successfully.
 * Signing happens client-side via the authenticated user client, which works
 * because the buckets grant authenticated SELECT (the same permission the
 * existing `.download()` calls rely on).
 */
import { getSupabase } from "../components/supabaseClient";
import { bucketName_files } from "../components/env";

// Matches a Supabase public object URL → captures [bucket, path].
const PUBLIC_URL_RE = /\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/;

/**
 * Create a short-lived signed URL for an object in a (possibly private) bucket.
 * Returns null on failure (caller decides how to degrade).
 */
export async function getSignedUrl(bucket, path, expiresIn = 3600) {
  if (!bucket || !path) return null;
  try {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);
    if (error) {
      console.warn(
        "[signedUrls] createSignedUrl failed:",
        bucket,
        path,
        error.message,
      );
      return null;
    }
    return data?.signedUrl || null;
  } catch (e) {
    console.warn("[signedUrls] createSignedUrl threw:", e?.message);
    return null;
  }
}

/** Convenience for the common case: a path inside the `files` bucket. */
export async function getSignedFilesUrl(path, expiresIn = 3600) {
  return getSignedUrl(bucketName_files || "files", path, expiresIn);
}

/**
 * Convert a stored Supabase *public* URL into a SIGNED URL for the same object,
 * so links saved before the bucket went private keep working with NO data
 * migration. Inputs that aren't a public storage URL (already-signed URLs,
 * blob:/data: URLs, external links) are returned unchanged. Falls back to the
 * original URL if signing fails, so callers never crash.
 */
export async function toSignedUrl(publicUrl, expiresIn = 3600) {
  if (!publicUrl || typeof publicUrl !== "string") return publicUrl;
  const m = publicUrl.match(PUBLIC_URL_RE);
  if (!m) return publicUrl; // not a public storage URL — leave as-is
  const bucket = m[1];
  const rawPath = m[2].split("?")[0];
  let path;
  try {
    path = decodeURIComponent(rawPath);
  } catch {
    // Malformed %-escape in the stored URL (e.g. a literal "%" in a filename).
    // Use the raw path rather than throwing — callers expect a graceful return.
    path = rawPath;
  }
  const signed = await getSignedUrl(bucket, path, expiresIn);
  return signed || publicUrl;
}

export default { getSignedUrl, getSignedFilesUrl, toSignedUrl };
